import Vue from 'vue';
import Vuex from 'vuex';
import { mapActions, mapMutations } from 'vuex';
import axios from 'axios';
import VueAxios from 'vue-axios';
import VueGoogleCharts from 'vue-google-charts';
import vueKeycloak from 'vue-keycloak';
import vueCountryRegionSelect from 'vue-country-region-select';
import App from './App.vue';
import vuetify from './plugins/vuetify';
import router from './router';
import EventBus from '@/EventBus';
import keycloakOptions from '../keycloak.json';
import PermissionHelper from './helpers/PermissionHelper';
import messages from './constants/messages';
import preferences from './constants/preferences';
import enums from './constants/Enums';
import GlobalComponents from './globalComponents';
import GlobalServices from './globalServices';
import mixin from './mixin';
import filters from './filters';
import store from './store';
import rapid7 from './rapid7';
import Enums from './constants/Enums';
import images from './constants/images';
Vue.use(Vuex);

Vue.prototype.$can = PermissionHelper;
Vue.prototype.$message = messages;
Vue.prototype.$preference = preferences;
Vue.prototype.$enums = enums;
Vue.prototype.$services = GlobalServices;
Vue.prototype.$images = images;
// Common js codes/utilities can be written here
Vue.mixin(mixin);
Vue.mixin({ filters });

Vue.config.productionTip = false;
Vue.use(VueAxios, axios);

Vue.use(vueCountryRegionSelect);

Vue.use(vueKeycloak, {
  keycloakOptions: keycloakOptions[process.env.VUE_APP_KEYCLOAK_ENV],
  keycloakInitOptions: {
    onLoad: 'login-required',
    promiseType: 'native'
  }
});
Vue.use(rapid7);
Vue.use(GlobalComponents);
new Vue({
  VueGoogleCharts,
  router: router(),
  store,
  vuetify,

  render: h => h(App),
  created() {
    // read value from localstorage here and disable appropriate theme
    const theme = localStorage.getItem('selectedTheme');
    if (theme === null || theme === 'null' || theme === 'Light') {
      document.getElementById('themeLight').removeAttribute('disabled');
      document.getElementById('themeDark').setAttribute('disabled', '');
    } else {
      document.getElementById('themeDark').removeAttribute('disabled');
      document.getElementById('themeLight').setAttribute('disabled', '');
    }
  },
  methods: {
    ...mapMutations([
      'setAllPermissions',
      'setSubscriptionPermissions',
      'setCurrentOrgActiveConnectors',
      'setSingleSignOnStatus',
      'setUserInfo',
      'setOnboardingStatus',
      'setOrgData'
    ]),

    ...mapMutations('discovery', ['setOpenProjects']),
    ...mapActions('preferences', ['getPreferences']),
    ...mapMutations('notifications', ['setNotiClientToken']),
    ...mapMutations(['setSelectedOrg']),
    hasPermission(route) {
      if (route.meta?.permission) {
        if (this.checkPermissions(route.meta.permission)) {
          return true;
        }
        this.snackbar('error', this.$message.NO_PERMISSION);
        return false;
      }
      return true;
    },
    async getUserPermissions(userId) {
      let permissions = await this.$services.user.getUserInfo(
        'userPermissions',
        {
          orgId: userId
        }
      );
      if (!permissions.data.success) {
        this.snackbar('error', this.$message.CANNOT_GET_PERMISSIONS);
        return;
      }
      if (!permissions.data.body.length) {
        this.snackbar('error', this.$message.CANNOT_GET_PERMISSIONS);
        return;
      }
      if (
        !permissions.data.body[0].user ||
        (permissions.data.body[0].user &&
          !permissions.data.body[0].user.permissions)
      ) {
        this.snackbar('error', this.$message.CANNOT_GET_PERMISSIONS);
        return;
      }
      return permissions.data.body[0].user.permissions;
    },
    // To get trial expiry data and setting in store inside subscription
    async getTrialExpiry() {
      try {
        const trialExpiryResponse = await this.$services.billing.getTrialExpiryData();
        const trialExpiryData = this.parseResponse(trialExpiryResponse)?.[0];
        if (trialExpiryData) {
          this.$store.dispatch('SET_SUBSCRIPTIONS', {
            createdAt: trialExpiryData?.createdAt,
            trialEndedAt: trialExpiryData?.expireAt,
            productHandle: trialExpiryData?.handle
          });
        }
      } catch (error) {
        this.snackbar('error', error);
      }
    },
    async initPreferences() {
      try {
        // get preferences
        await this.getPreferences();
        await this.onOrgChange();
        // hit expiry call only for subscribed orgs
        // and also checking for permission can_manage_subscription || can_view_subscription
        if (
          !this.isCurrentOrgLegacy &&
          (this.$can('view', 'subscription') ||
            this.$can('manage', 'subscription'))
        ) {
          await this.getTrialExpiry();
          await this.getUserSubscription('SET_BOTH');
        } else {
          this.$store.dispatch('SET_SUBSCRIPTIONS', {});
        }
        const currentOrgConnectors = await this.connectorStatus(
          null,
          this.orgId
        );
        this.setCurrentOrgActiveConnectors(currentOrgConnectors);
        EventBus.$emit('userMounted', true);
        await this.canAccessPage();
      } catch (err) {
        console.log(err);
      }
    },
    setLocalStorage(userId) {
      localStorage.setItem('userId', userId);

      store.state.orgId = this.$keycloak.profile.attributes.organisation[0];
      store.state.orgName = this.$keycloak.profile.attributes.organisationName[0];
      let stringyFiedData = JSON.stringify(this.$keycloak.idTokenParsed);
      localStorage.setItem('userData', stringyFiedData);

      /*
        1- in case of fresh login, make currentlySelectedOrgId same as logged in user's orgId
        2- in case of page reload, do nothing so that if user is on settings page and if he goes to Organisations, same org is selected in left menu
      */
      if (this.isNewLogin()) {
        this.setSelectedOrg(this.orgId);
      } else {
        // it is not a new login, so there will already be a selected org id in store
        // still if due to any edge case there is not one, we will set the default
        if (!this.currentlySelectedOrgId) {
          this.setSelectedOrg(this.orgId);
        }
      }
    },
    isNewLogin() {
      try {
        // since document.referrer is inconsistent in different browsers, first create a URL object out of it
        const refererInfo = new URL(document.referrer);
        return refererInfo.host === process.env.VUE_APP_SSO_HOST;
      } catch (err) {
        return true;
      }
    },
    async getUserDetails(userId) {
      const userInfo = await this.$services.user.userTruncatedDetails(userId);
      let userData = userInfo.data.body[0].user;
      this.setUserInfo(userData);
    },

    async initUser(profile) {
      store.state.sessionId = this.$keycloak.sessionId;

      this.setAllPermissions([]);
      let userPermissions = this.getUserPermissions(profile.username);
      this.getUserDetails(profile.username);

      try {
        let permissions = await userPermissions;
        this.setAllPermissions(permissions);
        if (this.$can('view', 'organisation')) {
          const orgDetailsResponse = await this.getOrganisationDetails();
          this.setOrgData(orgDetailsResponse);
          const onboardingThroughMailsphere = orgDetailsResponse.organisationCustomData.find(
              customData => customData?.key === 'onboardingThroughMailsphere'
            );
          if (!onboardingThroughMailsphere) {
            permissions = [...permissions, 'can_view_email_gateway'];
            this.setAllPermissions(permissions);
          }
          const subscriptionPermissions = [
            'can_manage_subscription',
            'can_manage_child_subscription',
            'can_view_child_subscription',
            'can_view_subscription'
          ];
          const subscriptionPermissionsAlreadyHave = permissions.reduce(
            (acc, permission) => {
              if (subscriptionPermissions.includes(permission)) {
                acc.push(permission);
              }
              return acc;
            },
            []
          );
          this.setSubscriptionPermissions(subscriptionPermissionsAlreadyHave);
        }

        this.$nextTick(async () => {
          if (this.isNewLogin()) {
            // means returning from SSO i.e. fresh login
            // clear list of open projects
            this.setNotiClientToken({});
            this.setOpenProjects([]);
          }
          this.setLocalStorage(profile.username);
          await this.initPreferences();
        });
      } catch {
        this.$nextTick(() => {
          this.setLocalStorage(profile.username);
          // proceed to application
          EventBus.$emit('userMounted', true);
          this.canAccessPage();
        });
      }
    },
    async getOrganisationDetails() {
      let orgDetails = {};
      let orgDetailsResponse = await this.$services.user.getUserOrg(
        this.$keycloak.profile.attributes.organisation[0]
      );
      orgDetails = orgDetailsResponse.data.body[0].organisation;
      return orgDetails;
    },
    async canAccessPage() {
      const onboardStatusResponse = await this.$services.user.getOnbardingStatus(
        this.orgId
      );
      this.setOnboardingStatus(onboardStatusResponse.data.body);
      // redirecting user to trial expired page when trial get expired
      if (this.isTrialEnded()) {
        this.snackbar('error', this.$message?.TRIAL_EXPIRED_NOTIFICATION);
        this.$router.replace('/expire/pack');
      } else if (
        onboardStatusResponse.data.body === Enums.onboardingStatus.done
      ) {
        this.initalRoute();
      } else if (
        onboardStatusResponse.data.body === Enums.onboardingStatus.fourth
      ) {
        const syncResponse = await this.$services.onboarding.onboardingOrg(
          undefined,
          'poll'
        );

        const jobStatus = syncResponse?.data?.response?.status?.status;
        if (jobStatus === 'COMPLETED') {
          this.snackbar('info', messages.ONBOARDING_COMPLETED, 0);
          await this.$services.user.setOnboardingStatus(undefined, 'ONBOARDED');
          this.setOnboardingStatus('ONBOARDED');
        } else if (jobStatus === 'FAILED') {
          this.snackbar('info', messages.ONBOARDING_FAILED, 0);
        } else {
          this.snackbar('info', messages.ONBOARDING_STARTED, 0);
        }
        this.initalRoute();
      } else if (
        this.$route.path !== '/connector/callback' &&
        this.$route.path !== '/connector/success' &&
        this.$route.path !== '/connector/failure'
      ) {
        this.$router.replace('/getting-started');
      }
    },
    intialiseInterceptor() {
      let self = this;
      var tokenTimeOutWait = 1200;
      axios.interceptors.request.use(
        async function(config) {
          if (self.$keycloak.isTokenExpired()) {
            try {
              await self.refreshToken(tokenTimeOutWait);
              config.headers = config.headers.Authorization
                ? config.headers
                : self.getHeaders();
              return config;
            } catch (err) {
              return Promise.reject(err);
            }
          } else {
            config.headers = config.headers.Authorization
              ? config.headers
              : self.getHeaders();
            return config;
          }
        },
        error => {
          return Promise.reject(error);
        }
      );
    }
  },
  mounted: function() {
    this.intialiseInterceptor();
  },
  beforeCreate() {
    this.$keycloak.onAuthSuccess = () => {
      this.$keycloak
        .loadUserProfile()
        .success(async res => {
          await this.initUser(res);
          this.$router.start();
        })
        .error(function() {});
    };
  }
}).$mount('#app');
